import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEOHead from '../components/SEOHead';
import { personalInfo } from '../data/mock';
import { Award, GraduationCap, Globe2, Cpu, ArrowRight } from 'lucide-react';

const accent = '#d4af37';
const navy = '#1a2332';

const Section = ({ title, children, icon: Icon }) => (
  <div className="mb-12">
    <div className="mb-4 flex items-center gap-3">
      {Icon && (
        <span className="inline-flex rounded-full p-2" style={{ backgroundColor: `${accent}20` }}>
          <Icon className="h-6 w-6" style={{ color: accent }} />
        </span>
      )}
      <h2 className="font-serif text-2xl font-bold md:text-3xl" style={{ color: navy }}>{title}</h2>
    </div>
    <div className="space-y-4 text-base leading-relaxed text-gray-700">{children}</div>
  </div>
);

const BiographyPage = () => {
  return (
    <div className="min-h-screen bg-white" data-testid="biography-page">
      <SEOHead
        title="Biography | Venkat Swaroop — Director of Operations & Founder of RecruiterIQ"
        description="Executive biography of Venkat Swaroop (Venkata Jyothi Swaroop Vemparala): global business leader, Director of Operations at TekWissen Group, founder of RecruiterIQ, and award-winning talent acquisition leader."
        url="https://venkatswaroop.in/biography"
      />
      <Header />

      <section className="pt-32 pb-16 text-white" style={{ backgroundColor: navy }}>
        <div className="container mx-auto px-6 text-center">
          <h1 className="mb-4 font-serif text-4xl font-bold sm:text-5xl">Biography</h1>
          <div className="mx-auto h-1 w-24 rounded" style={{ backgroundColor: accent }}></div>
          <p className="mx-auto mt-6 max-w-2xl text-base text-white/80 md:text-lg">
            The career, leadership, and innovation journey of Venkat Swaroop.
          </p>
        </div>
      </section>

      <section className="py-16">
        <div className="container mx-auto grid max-w-6xl gap-12 px-6 lg:grid-cols-3">
          {/* Portrait + quick facts */}
          <aside className="lg:col-span-1">
            <img
              src={personalInfo.image}
              alt={personalInfo.name}
              className="mx-auto mb-6 h-64 w-64 rounded-full border-4 object-cover shadow-xl"
              style={{ borderColor: accent }}
            />
            <div className="rounded-xl border p-6" style={{ borderColor: `${accent}40` }}>
              <h3 className="mb-4 font-serif text-xl font-bold" style={{ color: navy }}>At a glance</h3>
              <ul className="space-y-3 text-sm text-gray-700">
                <li><span className="font-semibold" style={{ color: navy }}>Role:</span> Director of Operations, TekWissen Group</li>
                <li><span className="font-semibold" style={{ color: navy }}>Founder:</span> RecruiterIQ</li>
                <li><span className="font-semibold" style={{ color: navy }}>Education:</span> {personalInfo.education.degree}, {personalInfo.education.institution}</li>
                <li><span className="font-semibold" style={{ color: navy }}>Experience:</span> 15+ years</li>
                <li><span className="font-semibold" style={{ color: navy }}>Markets:</span> US, Canada, UK, EMEA, India, Australia</li>
                <li><span className="font-semibold" style={{ color: navy }}>Base:</span> {personalInfo.location}</li>
              </ul>
            </div>
          </aside>

          {/* Long-form bio */}
          <div className="lg:col-span-2">
            <Section title="Overview" icon={Globe2}>
              <p>
                Venkat Swaroop (Venkata Jyothi Swaroop Vemparala) is a global business leader who unites
                operational excellence with technology-driven innovation. As Director of Operations at
                TekWissen Group, he leads global delivery operations and talent acquisition with over 15
                years of experience, having served 100+ Fortune 500 clients and led teams of 1000+
                professionals across the United States, Canada, the United Kingdom, EMEA, India, and Australia.
              </p>
            </Section>

            <Section title="Education" icon={GraduationCap}>
              <p>
                Venkat holds an {personalInfo.education.degree} from {personalInfo.education.institution},
                which grounds his data-informed, business-first approach to operations and talent strategy.
              </p>
            </Section>

            <Section title="Career Journey" icon={Globe2}>
              <p>
                Venkat progressed through increasingly senior operational leadership roles at TekWissen
                Group — from Deputy Manager of Operations &amp; Staffing, to Senior Program Manager, to
                Associate Director, and now Director of Operations. Along the way he built and scaled
                high-performing delivery teams, managed multi-million dollar budgets, and partnered with
                C-suite executives of Fortune 500 clients to align talent strategies with business outcomes.
                He specializes in Staff Augmentation, RPO, Contingent Workforce Solutions, and Direct Hire
                models, generating $15M+ in annual revenue across global delivery operations.
              </p>
              <RouterLink to="/career" className="inline-flex items-center gap-2 font-semibold hover:underline" style={{ color: accent }}>
                View the full career timeline <ArrowRight className="h-4 w-4" />
              </RouterLink>
            </Section>

            <Section title="Technology &amp; Innovation" icon={Cpu}>
              <p>
                Venkat is the founder of <strong>RecruiterIQ</strong>, an AI-powered recruitment intelligence
                and talent sourcing platform trusted by 10,000+ recruiters worldwide. RecruiterIQ brings
                together AI market intelligence, advanced candidate sourcing, SmartMatch scoring, and Talent
                Acquisition certifications. Beyond RecruiterIQ, he has championed the development of internal
                tools and AI-driven applications at TekWissen to streamline and modernize talent operations.
              </p>
              <RouterLink to="/recruiteriq" className="inline-flex items-center gap-2 font-semibold hover:underline" style={{ color: accent }}>
                Explore RecruiterIQ <ArrowRight className="h-4 w-4" />
              </RouterLink>
            </Section>

            <Section title="Recognition" icon={Award}>
              <p>
                Venkat is an award-winning leader recognized across the staffing and HR industry — including
                Top 100 Influencing Leaders (Fox Story India, 2025), Top Staffing Leaders to Watch
                (Candidate.ly, 2024 &amp; 2025), Top 10 Growing Influential Leaders (FoxStory, 2023), 40 Under
                40 Dynamic Leaders (HRAI, 2023), Top 100 Great People Managers (Forbes India, 2022), and
                World&apos;s Best Recruiters (RChilli, 2021).
              </p>
            </Section>

            <Section title="Leadership Philosophy">
              <p>
                Venkat&apos;s leadership centers on empowerment, collaboration, and continuous growth —
                fostering environments where individuals and organizations thrive together. He believes the
                future of talent lies at the intersection of operational discipline and intelligent
                technology, and he leads accordingly.
              </p>
            </Section>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default BiographyPage;
